import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getSettings } from '../util/getSettings';
import { Filters } from '../util/constants';
import FilterModal from './filter-modal';
import FilterButton from './filter-button';
import StateFilter from './filters/state-filter';
import MetroFilter from './filters/metro-filter';
import CityFilter from './filters/city-filter';
import PriceRange from './filters/price-range';
import SquareFeet from './filters/square-feet';
import HomeTypes from './filters/home-types';
import BedsBaths from './filters/beds-baths';
import ParkingBays from './filters/parking-bays';
import NumberFloors from './filters/number-floors';
import MpcFilter from './filters/mpc-filter';
import ResetFilter from './filters/reset-filter';
import { getFiltersFromSource } from '../util/filters';
import { QsStateManager } from '../util/urls';
import { formatNumberToDollar } from '../util/formatNumber';
import { ReactSimpleMap } from '../util/reactSimpleMap';
import {
  priceRangeUpdated,
  bedBathsUpdated,
  homeTypesUpdated,
  brandUpdated,
  squareFeetUpdated,
  baysUpdated,
  floorsUpdated,
  mpcOnlyUpdated,
  filtersUpdated,
  resetClicked,
  quickMoveInUpdated
} from '../redux/actions/filters';
import { filterDefaults } from '../util/filters';
import Switch from './shared/switch';
import { setRegionSearchData } from '../redux/actions/regionData';
import { regionDefaults } from '../util/regions';

const Filter = ({ filterType, positionLeft, hideFooter }) => {
  const filterContainerId = `${filterType}_CONTAINER`
  const filters = useSelector((state) => state.filters);
  const regionData = useSelector((state) => state.regionData);
  const [currentFilters, setCurrentFilters] = useState(getFiltersFromSource(filters));
  const [isOpen, setIsOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [filterChanged, setFilterChanged] = useState(false);
  const labels = getSettings(['century', 'labels'], true);
  const settings = getSettings(['century', 'settings']);
  const dispatch = useDispatch();
  
  const {
    minPrice: currentMinPrice,
    maxPrice: currentMaxPrice,
    bedrooms: currentBedrooms,
    bathrooms: currentBathrooms,
    homeTypes: currentHomeTypes,
    brand: currentBrand,
    minSquareFeet: currentMinSquareFeet,
    maxSquareFeet: currentMaxSquareFeet,
    bays: currentBays,
    floors: currentFloors,
    mpcOnly: currentMpcFilter,
    sortBy: currentSortBy,
    asc: currentAsc
  } = currentFilters;

  const {
    minPrice: defaultMinPrice,
    maxPrice: defaultMaxPrice,
    bedrooms: defaultBedrooms,
    bathrooms: defaultBathrooms,
    homeTypes: defaultHomeTypes,
    brand: defaultBrand,
    minSquareFeet: defaultMinSquareFeet,
    maxSquareFeet: defaultMaxSquareFeet,
    bays: defaultBays,
    floors: defaultFloors,
    mpcOnly: defaultMpcOnly,
    sortBy: defaultSortBy,
    asc: defaultAsc
  } = filterDefaults;

  const setLabelsAndCurrentFilters = (
    beds,
    baths,
    minPrice,
    maxPrice,
    homeTypes,
    brand,
    minSqFt,
    maxSqFt,
    bays,
    floors,
    mpc
  ) => {
    if (filterType === Filters.PRICE_RANGE) {
      formatPriceRangeFilterLabel(minPrice, maxPrice);
      setCurrentFilters({ ...currentFilters, minPrice: minPrice, maxPrice: maxPrice });
    }
    if (filterType === Filters.BEDS_BATHS) {
      formatBedsBathsFilterLabel(beds, baths);
      setCurrentFilters({ ...currentFilters, bedrooms: beds, bathrooms: baths });
    }
    if (filterType === Filters.HOME_TYPES) {
      formatHomeTypesFilterLabel(homeTypes);
      setCurrentFilters({ ...currentFilters, homeTypes: homeTypes.toString() });
    }
    if (filterType === Filters.BRANDS) {
      formatBrandFilterAppearance(brand);
      setCurrentFilters({ ...currentFilters, brand: brand });
    }
    if (filterType === Filters.MORE) {
      setCurrentFilters({
        ...currentFilters,
        minSquareFeet: minSqFt,
        maxSquareFeet: maxSqFt,
        bays: bays,
        floors: floors,
        mpcOnly: mpc
      });
      setMoreFilterStates(minSqFt, maxSqFt, bays, floors, mpc);
    }
  };

  useEffect(() => {
    setLabelsAndCurrentFilters(
      currentBedrooms,
      currentBathrooms,
      currentMinPrice,
      currentMaxPrice,
      currentHomeTypes,
      currentBrand,
      currentMinSquareFeet,
      currentMaxSquareFeet,
      currentBays,
      currentFloors,
      currentMpcFilter
    );
  }, []);

  useEffect(() => {
    setLabelsAndCurrentFilters(
      filters.bedrooms,
      filters.bathrooms,
      filters.minPrice,
      filters.maxPrice,
      filters.homeTypes,
      filters.brand,
      filters.minSquareFeet,
      filters.maxSquareFeet,
      filters.bays,
      filters.floors,
      filters.mpcOnly
    );
  }, [filters.timeStamp, regionData.timeStamp]);

  useEffect(() => {
    if (filters.resetTimeStamp) {
      setCurrentFilters(filterDefaults);
      filterType === Filters.PRICE_RANGE && setFilterStates(labels.priceFilterLabel, false, false);
      filterType === Filters.BEDS_BATHS && setFilterStates(labels.bedsAndBathsFilterLabel, false, false);
      filterType === Filters.HOME_TYPES && setFilterStates(labels.homeTypeFilterLabel, false, false);
      filterType === Filters.BRANDS && setFilterStates(labels.brandFilterLabel, false, false);
      filterType === Filters.MORE && setFilterStates(labels.moreLabel, false, false);
    }
  }, [filters.resetTimeStamp]);

  useEffect(() => {
    setFilterType(filterType);
    if (filterType == Filters.STATE_FILTER || filterType == Filters.METRO_FILTER) {
      setIsOpen(false);
    }
  }, [filterType, regionData.stateName, regionData.metroName]);

  useEffect(() => {
    if (filterType === Filters.STATE_FILTER && isOpen) {
      const searchString = new QsStateManager().getSearchString()
      const simpleMap = new ReactSimpleMap("state_filter_simplemap", settings.initState)
      simpleMap.createMap()
      simpleMap.configureFYHSettings(searchString)
      simpleMap.init()
    }
  }, [isOpen]);

  const toggleFilter = () => {
    setIsOpen(!isOpen);
  };

  const closeFilter = () => {
    setIsOpen(false);
    setCurrentFilters(filters);
  };

  const setFilterType = (filterType) => {
    switch (filterType) {
      case Filters.STATE_FILTER:
        setLabel(settings.stateName || labels.stateFilterLabel);
        setFilterStates(settings.stateName || labels.stateFilterLabel, false, false);
        return;
      case Filters.METRO_FILTER:
        setLabel(settings.metroName.length ? settings.metroName : labels.areaFilterLabel);
        return;
      case Filters.CITY_FILTER:
        setLabel(settings.cityName.length ? settings.cityName : labels.cityFilterLabel);
        return;
      case Filters.PRICE_RANGE:
        if (currentMinPrice === defaultMinPrice && currentMaxPrice === defaultMaxPrice) {
          setLabel(labels.priceFilterLabel);
        }
        return;
      case Filters.BEDS_BATHS:
        if (currentBedrooms == defaultBedrooms && currentBathrooms === defaultBathrooms) {
          setLabel(labels.bedsAndBathsFilterLabel);
        }
        return;
      case Filters.HOME_TYPES:
        if (currentHomeTypes === defaultHomeTypes) {
          setLabel(labels.homeTypeFilterLabel);
        }
        return;
      case Filters.MORE:
        setLabel(labels.moreLabel);
        return;
    }
  };

  const onSave = () => {
    switch (filterType) {
      case Filters.PRICE_RANGE:
        return onPriceRangeSave();
      case Filters.BEDS_BATHS:
        return onBedBathSave();
      case Filters.HOME_TYPES:
        return onHomeTypesSave();
      case Filters.MORE:
        return onMoreSave();
    }
  };

  const onClear = () => {
    switch (filterType) {
      case Filters.PRICE_RANGE:
        return onPriceRangeClear();
      case Filters.BEDS_BATHS:
        return onBedBathClear();
      case Filters.HOME_TYPES:
        return onHomeTypeClear();
      case Filters.MORE:
        return onMoreClear();
    }
  };

  const setFilterStates = (labelValue, filterChangedValue, openValue) => {
    setLabel(labelValue);
    setFilterChanged(filterChangedValue);
    setIsOpen(openValue);
  };

  //PRICE RANGE
  const onPriceRangeChange = (minPrice, maxPrice) => {
    setCurrentFilters((prevState) => ({ ...prevState, minPrice: minPrice, maxPrice: maxPrice }));
  };

  const formatPriceRangeFilterLabel = (minPrice, maxPrice) => {
    if (minPrice === settings.minPrice && maxPrice === settings.maxPrice) {
      setFilterStates(labels.priceFilterLabel, false, false);
    } else {
      const minString = formatNumberToDollar(minPrice);
      const maxString = formatNumberToDollar(maxPrice);
      setFilterStates(`${minString} - ${maxString}`, true, false);
    }
  };

  const onPriceRangeSave = () => {
    dispatch(priceRangeUpdated(currentMinPrice, currentMaxPrice));
    formatPriceRangeFilterLabel(currentMinPrice, currentMaxPrice);
    dispatch(filtersUpdated());
    new QsStateManager().addParam('np', currentMinPrice).addParam('xp', currentMaxPrice).addFilters(filters).doUpdate();
  };

  const onPriceRangeClear = () => {
    dispatch(priceRangeUpdated(defaultMinPrice, defaultMaxPrice));
    setCurrentFilters({ ...currentFilters, minPrice: defaultMinPrice, maxPrice: defaultMaxPrice });
    setFilterStates(labels.priceFilterLabel, false, false);
    new QsStateManager().addParam('np', defaultMinPrice).addParam('xp', defaultMaxPrice).addFilters(filters).doUpdate();
    if (currentFilters.minPrice !== defaultMinPrice || currentFilters.maxPrice !== defaultMaxPrice) {
      dispatch(filtersUpdated());
    }
  };

  //BEDS BATHS
  const onBedBathChange = (bedrooms, bathrooms) => {
    setCurrentFilters({ ...currentFilters, bedrooms: bedrooms, bathrooms: bathrooms });
  };

  const formatBedsBathsFilterLabel = (bedrooms, bathrooms) => {
    if (bedrooms === defaultBedrooms && bathrooms === defaultBathrooms) {
      setFilterStates(labels.bedsAndBathsFilterLabel, false, false);
    } else {
      setFilterStates(`${bedrooms}+BR / ${bathrooms}+BA`, true, false);
    }
  };

  const onBedBathSave = () => {
    dispatch(bedBathsUpdated(currentBedrooms, currentBathrooms));
    formatBedsBathsFilterLabel(currentBedrooms, currentBathrooms);
    dispatch(filtersUpdated());
    new QsStateManager()
      .addParam('br', currentBedrooms)
      .addParam('ba', currentBathrooms)
      .addFilters(filters)
      .doUpdate();
  };

  const onBedBathClear = () => {
    dispatch(bedBathsUpdated(defaultBedrooms, defaultBathrooms));
    setCurrentFilters({ ...currentFilters, bedrooms: defaultBedrooms, bathrooms: defaultBathrooms });
    setFilterStates(labels.bedsAndBathsFilterLabel, false, false);
    new QsStateManager()
      .addParam('br', defaultBedrooms)
      .addParam('ba', defaultBathrooms)
      .addFilters(filters)
      .doUpdate();
    if (currentFilters.bathrooms !== defaultBathrooms || currentFilters.bedrooms !== defaultBedrooms) {
      dispatch(filtersUpdated());
    }
  };

  //HOME TYPES
  const onHomeTypesChange = (homeTypes) => {
    setCurrentFilters({ ...currentFilters, homeTypes: homeTypes.toString() });
  };

  const formatHomeTypesFilterLabel = (homeTypes) => {
    if (homeTypes) {
      const homeTypesArray = homeTypes.split(',');
      const homeTypesLabel =
        homeTypesArray.length > 1
          ? `${homeTypesArray[0]} +`
          : homeTypesArray[0] === ''
          ? labels.homeTypeFilterLabel
          : `${homeTypesArray[0]}`;
      let filterChangedValue = false;
      if (homeTypesArray.length >= 1 && homeTypesArray[0] !== '') {
        filterChangedValue = true;
      }
      setFilterStates(homeTypesLabel, filterChangedValue, false);
    } else {
      setFilterStates(labels.homeTypeFilterLabel, false, false);
    }
  };

  const onHomeTypesSave = () => {
    dispatch(homeTypesUpdated(currentHomeTypes));
    formatHomeTypesFilterLabel(currentHomeTypes);
    dispatch(filtersUpdated());
    new QsStateManager().addParam('ht', currentHomeTypes).addFilters(filters).doUpdate();
  };

  const onHomeTypeClear = () => {
    dispatch(homeTypesUpdated(defaultHomeTypes));
    setCurrentFilters({ ...currentFilters, homeTypes: defaultHomeTypes });
    setFilterStates(labels.homeTypeFilterLabel, false, false);
    new QsStateManager().addParam('ht', '').addFilters(filters).doUpdate();
    if (currentFilters.defaultHomeTypes !== defaultHomeTypes) {
      dispatch(filtersUpdated());
    }
  };

  //MORE FILTERS
  const onSquareFeetRangeChange = (minFeet, maxFeet) => {
    setCurrentFilters((prevState) => ({ ...prevState, minSquareFeet: minFeet, maxSquareFeet: maxFeet }));
  };

  const onParkingBaysChange = (bays) => {
    setCurrentFilters({ ...currentFilters, bays: bays });
  };

  const onFloorsChange = (floors) => {
    setCurrentFilters({ ...currentFilters, floors: floors });
  };

  const onMpcFilterChange = (mpcOnly) => {
    setCurrentFilters({ ...currentFilters, mpcOnly: mpcOnly });
  };

  const filtersChanged = (minSqFt, maxSqFt, bays, floors, mpc) => {
    let filtersChangedCount = 0;
    if (minSqFt !== defaultMinSquareFeet || maxSqFt !== defaultMaxSquareFeet) {
      filtersChangedCount++;
    }
    if (bays !== defaultBays) {
      filtersChangedCount++;
    }
    if (floors !== defaultFloors) {
      filtersChangedCount++;
    }
    if (mpc) {
      filtersChangedCount++;
    }
    return filtersChangedCount > 0;
  };

  const setMoreFilterStates = (minSqFt, maxSqFt, bays, floors, mpc) => {
    setFilterStates(labels.moreLabel, filtersChanged(minSqFt, maxSqFt, bays, floors, mpc), false);
  };

  const onMoreSave = () => {
    setMoreFilterStates(currentMinSquareFeet, currentMaxSquareFeet, currentBays, currentFloors, currentMpcFilter);
    dispatch(baysUpdated(currentBays));
    dispatch(floorsUpdated(currentFloors));
    dispatch(squareFeetUpdated(currentMinSquareFeet, currentMaxSquareFeet));
    dispatch(mpcOnlyUpdated(currentMpcFilter));
    dispatch(filtersUpdated());
    new QsStateManager()
      .addParam('nf', currentMinSquareFeet)
      .addParam('xf', currentMaxSquareFeet)
      .addParam('by', currentBays)
      .addParam('fl', currentFloors)
      .addParam('pc', currentMpcFilter)
      .addFilters(filters)
      .doUpdate();
  };

  const onMoreClear = () => {
    dispatch(squareFeetUpdated(defaultMinSquareFeet, defaultMaxSquareFeet));
    dispatch(baysUpdated(defaultBays));
    dispatch(floorsUpdated(defaultFloors));
    dispatch(mpcOnlyUpdated(defaultMpcOnly));
    setCurrentFilters({
      ...currentFilters,
      minSquareFeet: defaultMinSquareFeet,
      maxSquareFeet: defaultMaxSquareFeet,
      bays: defaultBays,
      floors: defaultFloors,
      mpcOnly: defaultMpcOnly
    });
    setFilterStates(labels.moreLabel, false, false);
    dispatch(filtersUpdated());
    new QsStateManager()
      .addParam('nf', defaultMinSquareFeet)
      .addParam('xf', defaultMaxSquareFeet)
      .addParam('by', defaultBays)
      .addParam('fl', defaultFloors)
      .addParam('pc', defaultMpcOnly)
      .addFilters(filters)
      .doUpdate();
  };

  //RESET FILTER
  const onReset = () => {
    new QsStateManager()
      .addParam('np', defaultMinPrice)
      .addParam('xp', defaultMaxPrice)
      .addParam('br', defaultBedrooms)
      .addParam('ba', defaultBathrooms)
      .addParam('ht', '')
      .addParam('nd', '')
      .addParam('nf', defaultMinSquareFeet)
      .addParam('xf', defaultMaxSquareFeet)
      .addParam('by', defaultBays)
      .addParam('fl', defaultFloors)
      .addParam('pc', defaultMpcOnly)
      .addParam('qm', false)
      .addFilters(filters)
      .doUpdate();
    dispatch(priceRangeUpdated(defaultMinPrice, defaultMaxPrice));
    dispatch(bedBathsUpdated(defaultBedrooms, defaultBathrooms));
    dispatch(brandUpdated(defaultBrand));
    dispatch(homeTypesUpdated(defaultHomeTypes));
    dispatch(squareFeetUpdated(defaultMinSquareFeet, defaultMaxSquareFeet));
    dispatch(baysUpdated(defaultBays));
    dispatch(floorsUpdated(defaultFloors));
    dispatch(mpcOnlyUpdated(defaultMpcOnly));
    dispatch(quickMoveInUpdated(false));
    dispatch(resetClicked());
    dispatch(filtersUpdated());
    dispatch(setRegionSearchData(regionDefaults));
    document.querySelectorAll('.filter-container')[1].classList.remove('metro-selected');
    //select all brands when reset is triggered
    const el = document.getElementById('brandsRadioAlldesktop');
    if (el) {
      el.click();
    }
  };

  return (
    <>
      {filterType !== Filters.RESET && (
        <div className="filter-container">
          <FilterButton
            label={label}
            onClick={toggleFilter}
            active={isOpen || filterChanged}
            ariaExpanded={isOpen}
            ariaControl={filterContainerId}
          />
          <FilterModal
            open={isOpen}
            closeModal={closeFilter}
            onSave={onSave}
            onClear={onClear}
            hideFooter={hideFooter}
            positionLeft={positionLeft}
            stateFilter={filterType === Filters.STATE_FILTER}
            metroFilter={filterType === Filters.METRO_FILTER}
            cityFilter={filterType === Filters.CITY_FILTER}
            containerId={filterContainerId}
          >
            <Switch value={filterType}>
              <StateFilter value={Filters.STATE_FILTER} />
              <MetroFilter value={Filters.METRO_FILTER} />
              <CityFilter value={Filters.CITY_FILTER} />
              <PriceRange
                value={Filters.PRICE_RANGE}
                minPrice={currentMinPrice}
                maxPrice={currentMaxPrice}
                onChange={onPriceRangeChange}
              />
              <BedsBaths
                value={Filters.BEDS_BATHS}
                onChange={onBedBathChange}
                bedCount={currentBedrooms}
                bathCount={currentBathrooms}
              />
              <HomeTypes value={Filters.HOME_TYPES} onChange={onHomeTypesChange} selectedHomeTypes={currentHomeTypes} />
              <div className="more-filters-container" value={Filters.MORE}>
                <SquareFeet
                  minFeet={currentMinSquareFeet}
                  maxFeet={currentMaxSquareFeet}
                  onChange={onSquareFeetRangeChange}
                />
                <div className="filter-row align-items-stretch">
                  <ParkingBays currentBays={currentBays} onChange={onParkingBaysChange} />
                  <NumberFloors currentFloors={currentFloors} onChange={onFloorsChange} />
                </div>
                <MpcFilter currentMpcFilter={currentMpcFilter} onChange={onMpcFilterChange} />
              </div>
            </Switch>
          </FilterModal>
        </div>
      )}
      {filterType === Filters.RESET && (
        <div className="filter-container">
          <ResetFilter onReset={onReset} />
        </div>
      )}
    </>
  );
};

export default Filter;
